import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { TrainConfig, carOffset } from './train';
import { G } from './physics';

export interface CarG {
  car: number;
  vertical: number; // + = pressed into the seat; < 0 = airtime; > 6 dangerous
  lateral: number; // > 1.8 uncomfortable on steel
  longitudinal: number;
}

const tmpAccel = new Vector3();
const tmpKvec = new Vector3();
const tmpFelt = new Vector3();

/**
 * Felt acceleration per car: a_felt = a_track − g_vec, expressed in the car's
 * banked frame and divided by g. a_track = a_tangential·t̂ + v²·κ_vec.
 */
export function carGForces(
  runtime: TrackRuntime,
  train: TrainConfig,
  headS: number,
  v: number,
  aTangential: number,
  gravity = G,
): CarG[] {
  const out: CarG[] = [];
  for (let i = 0; i < train.cars; i++) {
    const s = runtime.centerline.normalizeS(headS + carOffset(train, i));
    const frame = runtime.frameAt(s);
    runtime.centerline.curvatureVecAt(s, tmpKvec);
    tmpAccel.copy(frame.tan).multiplyScalar(aTangential).addScaledVector(tmpKvec, v * v);
    tmpFelt.copy(tmpAccel);
    tmpFelt.y += gravity; // subtract g_vec = (0,−g,0)
    out.push({
      car: i,
      vertical: tmpFelt.dot(frame.up) / gravity,
      lateral: tmpFelt.dot(frame.right) / gravity,
      longitudinal: tmpFelt.dot(frame.tan) / gravity,
    });
  }
  return out;
}

/**
 * Sweep the whole circuit with an energy march and record speed and max-G per
 * arc-length bucket — powers the "G over the circuit" graph and validation.
 */
export function profileCircuit(
  runtime: TrackRuntime,
  train: TrainConfig,
  accel: (s: number, v: number) => number,
  startS: number,
  startV: number,
  step = 1,
): { s: number; v: number; g: CarG }[] {
  const L = runtime.totalLength;
  const samples: { s: number; v: number; g: CarG }[] = [];
  let v2 = startV * startV;
  for (let d = 0; d < L; d += step) {
    const s = runtime.centerline.normalizeS(startS + d);
    const v = Math.sqrt(Math.max(v2, 0.01));
    const a = accel(s, v);
    const g = carGForces(runtime, { ...train, cars: 1 }, s, v, a)[0];
    samples.push({ s, v, g });
    v2 = Math.max(v2 + 2 * a * step, 1); // clamp: minSpeed drive keeps it moving in reality
  }
  return samples;
}
